import { useState, createContext, useEffect, useRef, useContext, useMemo } from "react";
import { Navigate, Link, createBrowserRouter, RouterProvider, Outlet, useNavigate, useLocation } from 'react-router-dom';
// import App from './App'; // deprecated
import './App.css';
import { useMantineColorScheme } from "@mantine/core";

import { googleLogout, useGoogleLogin, GoogleLogin } from '@react-oauth/google';
import axios from 'axios';

import ArtEvent from './components/ArtEvent';
// import RecommendationEvents from './components/RecommendationEvents';
import UserPage from './components/UserPage';
import Users from './components/Users';
import Settings from './components/Settings';
import Notifications from './components/Notifications';
import PersonalArtEvents from './components/PersonalArtEvents';
import AddEvent from './components/AddEvent';
import ArtPage from './components/ArtPage';
import NewArtByLink from './components/NewArtByLink';

import axiosInstance, { baseURL } from './axiosInstance.js';
import MyUserContext from './myUserContext.js';
import * as oauth from './oauth.js';

import LogbookHistory from './components/LogbookHistory';
import LogbookPage from './components/LogbookPage';
import RecalcUser from './components/RecalcUser';

import ReactGA from 'react-ga4'; // Import Google Analytics
import NewUser from "./components/NewUser.js";
import { IconHome, IconSearch, IconArrowLeft } from "@tabler/icons-react";
import {
  AppShell,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Indicator,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
  PinInput,
  TextInput
} from "@mantine/core";

const MainPageUIContext = createContext(null);

// Module-level: survives Layout remounts caused by router recreation
let _sectionStartIdx = null;
let _navClicked = false;

const Layout = ({loggedIn}) => {
  const {
    selectedTab,
    setSelectedTab,
    repeatedEnter,
    setRepeatedEnter,
    myUserSettings,
    myUserInfo,
    unreadCount,
    isRussianSpeaking
  } = useContext(MainPageUIContext);

  const navigate = useNavigate();
  const location = useLocation();

  // Track section boundaries using browser history index (survives remounts)
  useEffect(() => {
    const currentIdx = window.history.state?.idx ?? 0;
    if (_navClicked) {
      _navClicked = false;
      _sectionStartIdx = currentIdx;
    } else if (_sectionStartIdx === null) {
      _sectionStartIdx = currentIdx;
    }
  }, [location]);

  const currentHistoryIdx = window.history.state?.idx ?? 0;
  const showBackButton = !_navClicked && _sectionStartIdx !== null && currentHistoryIdx > _sectionStartIdx;

  // Telegram Mini App back button
  useEffect(() => {
    const tgWebApp = window.Telegram?.WebApp;
    if (!tgWebApp || !tgWebApp.initData) return;
    if (showBackButton) {
      tgWebApp.BackButton?.show();
    } else {
      tgWebApp.BackButton?.hide();
    }
    const onBack = () => navigate(-1);
    tgWebApp.BackButton?.onClick(onBack);
    return () => tgWebApp.BackButton?.offClick(onBack);
  }, [showBackButton, navigate]);

  const isRussian = myUserSettings?.lang === "ru" || isRussianSpeaking;

  const avatarUrl = myUserSettings?.avatarUrl || "avatar_neutral.jpeg";
  const avatarSrc = avatarUrl.startsWith("http")
    ? `${baseURL}/avatarProxy?url=${encodeURIComponent(avatarUrl)}`
    : avatarUrl;

  const handleNavClick = (tabIndex) => {
    _navClicked = true;
    setRepeatedEnter(prev => prev + 1);
    setSelectedTab(tabIndex);
  };

  const notificationIcon = (
    <Indicator
      size={16}
      color="indigo"
      disabled={!unreadCount}
      label={unreadCount ? Math.min(unreadCount, 99) : undefined}
    >
      <IconHome size={22} stroke={1.5} />
    </Indicator>
  );

  const navItems = [
    {
      key: 0,
      label: isRussian ? "Лента" : "Home",
      to: "/notifications",
      icon: notificationIcon
    },
    {
      key: 1,
      label: isRussian ? "Поиск" : "Search",
      to: "/addEvent",
      icon: <IconSearch size={22} stroke={1.5} />
    },
    {
      key: 4,
      label: isRussian ? "Моя библиотека" : "My library",
      to: loggedIn ? `/users/${myUserInfo?.myUserId || ""}/likes` : "/",
      icon: (
        <Avatar
          src={avatarSrc}
          radius="xl"
          size={32}
        />
      )
    }
  ];

  const navButtonStyle = (isActive) => ({
    padding: '10px 14px',
    borderRadius: 999,
    transition: 'background-color 120ms ease',
    backgroundColor: isActive ? 'var(--mantine-primary-color-light)' : 'transparent',
  });

  const navIconStyle = (isActive) => ({
    width: 44,
    height: 44,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isActive ? 'var(--mantine-primary-color-light)' : 'var(--mantine-color-default)',
  });

  const NavButton = ({ item, layout }) => {
    const isActive = selectedTab === item.key;
    const labelSize = layout === "footer" ? "xs" : "sm";
    const labelWeight = isActive ? 600 : 500;
    return (
      <UnstyledButton
        component={Link}
        to={item.to}
        onClick={() => handleNavClick(item.key)}
        style={navButtonStyle(isActive)}
      >
        {layout === "footer" ? (
          <Stack gap={4} align="center">
            <Box style={navIconStyle(isActive)}>{item.icon}</Box>
          </Stack>
        ) : (
          <Group gap="sm" wrap="nowrap">
            <Box style={navIconStyle(isActive)}>{item.icon}</Box>
            <Text size={labelSize} fw={labelWeight} c={isActive ? undefined : "dimmed"}>
              {item.label}
            </Text>
          </Group>
        )}
      </UnstyledButton>
    );
  };

  const shellNavStyle = {
    backgroundColor: 'var(--mantine-color-body)',
    backdropFilter: 'blur(16px)',
  };

  const triggerFireworks = () => {
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const particles = [];
    const colors = ['#ff595e','#ffca3a','#8ac926','#1982c4','#6a4c93','#ff6b6b','#feca57','#48dbfb'];
    for (let i = 0; i < 5; i++) {
      const x = Math.random() * canvas.width;
      const y = canvas.height * 0.2 + Math.random() * canvas.height * 0.3;
      for (let j = 0; j < 40; j++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 5 + 2;
        particles.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1, color: colors[Math.floor(Math.random() * colors.length)], size: Math.random() * 3 + 1 });
      }
    }
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.life -= 0.015;
        if (p.life > 0) {
          alive = true;
          ctx.globalAlpha = p.life;
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      if (alive) requestAnimationFrame(animate);
      else document.body.removeChild(canvas);
    };
    requestAnimationFrame(animate);
  };

  return (
    <AppShell
      className="app-shell"
      padding={{ base: 0, sm: "md" }}
      header={{ height: 72 }}
      footer={{ height: "calc(80px + env(safe-area-inset-bottom))" }}
    >
      <AppShell.Header visibleFrom="sm" style={{ ...shellNavStyle, borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <Container size="sm" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" onClick={triggerFireworks} style={{ cursor: 'pointer', userSelect: 'none' }}>
              <img src="/logo_wowcontent.jpg" alt="WowContent" style={{ height: 32, borderRadius: 6 }} />
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {isRussian ? "Фильмы, книги, игры" : "Movies, books, games"}
              </Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              {navItems.map((item) => (
                <NavButton key={item.key} item={item} layout="header" />
              ))}
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      <AppShell.Footer hiddenFrom="sm" style={{ ...shellNavStyle, borderTop: '1px solid var(--mantine-color-default-border)', position: 'fixed', bottom: 0, left: 0, right: 0 }}>
        <Container size="lg" h="100%">
          <Group h="100%" justify="space-around">
            {navItems.map((item) => (
              <NavButton key={item.key} item={item} layout="footer" />
            ))}
          </Group>
        </Container>
      </AppShell.Footer>

      <AppShell.Main
        pt={{ base: "xs", sm: 80 }}
        pb={{ base: "calc(80px + env(safe-area-inset-bottom))", sm: "lg" }}
      >
        <Container size="lg" px={{ base: "xs", sm: "sm" }} py={{ base: 0, sm: "lg" }}>
          {showBackButton && (
            <div className="mb-1" style={{ maxWidth: 640, paddingLeft: 'clamp(4px, 5vw, 80px)' }}>
              <UnstyledButton onClick={() => navigate(-1)} style={{ padding: 4, borderRadius: 999 }}>
                <IconArrowLeft size={30} stroke={1.5} />
              </UnstyledButton>
            </div>
          )}
          <Outlet />
        </Container>
      </AppShell.Main>
    </AppShell>
  );
};

const TelegramLoginButton = ({ onAuth }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const callbackName = "__tg_login_callback_" + Date.now();
    window[callbackName] = (user) => {
      // Convert all values to strings for the backend.
      const data = {};
      for (const key in user) {
        data[key] = String(user[key]);
      }
      onAuth(data);
      delete window[callbackName];
    };
    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", "wowcontent_bot");
    script.setAttribute("data-size", "large");
    script.setAttribute("data-radius", "8");
    script.setAttribute("data-onauth", callbackName + "(user)");
    script.setAttribute("data-request-access", "write");
    script.async = true;
    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(script);
    return () => { delete window[callbackName]; };
  }, [onAuth]);
  return <div ref={containerRef} />;
};

const LoggedOutHome = () => {
  const {
    isRussianSpeaking,
    emailLogin,
    emailLoginCode,
    emailLoginMessage,
    emailLoginError,
    loginCodeRequested,
    updateMyUserInfo,
    handleEmailLoginChange,
    setEmailLoginCode,
    handleSendLoginCode,
    handleLoginWithCode
  } = useContext(MainPageUIContext);

  const handleTelegramLogin = (tgData) => {
    oauth.TelegramLoginWidgetAuth({
      tgData,
      onJWTObtained: () => {
        updateMyUserInfo();
        window.location.href = "/";
      }
    });
  };

  const renderLoginOptions = () => (
    <Stack gap="sm" align="center">
      <Box>
        <GoogleLogin
          onSuccess={(credentialResponse) => {
            oauth.ProvideCredentialToBackend({
              credential: credentialResponse.credential,
              onJWTObtained: () => {
                updateMyUserInfo();
                window.location.href = "/";
              }
            });
          }}
          onError={() => {
            console.log('Login Failed');
          }}
        />
      </Box>
      <TelegramLoginButton onAuth={handleTelegramLogin} />
      <Text size="sm" c="dimmed">
        {isRussianSpeaking ? "или через email" : "or via email"}
      </Text>
      <Group gap="sm" justify="center" wrap="wrap">
        <TextInput
          type="email"
          value={emailLogin}
          onChange={handleEmailLoginChange}
          placeholder="Email"
          radius="md"
          w={220}
          size="sm"
        />
        <Button
          onClick={handleSendLoginCode}
          variant="light"
          color="indigo"
          size="sm"
        >
          {loginCodeRequested
            ? isRussianSpeaking
              ? "Отправить код повторно"
              : "Send code again"
            : isRussianSpeaking
              ? "Продолжить"
              : "Continue"}
        </Button>
      </Group>
      {emailLoginMessage && !emailLoginError && (
        <Text size="sm" c="blue" ta="center">
          {emailLoginMessage}
        </Text>
      )}
      {loginCodeRequested && (
        <Group gap="sm" justify="center" wrap="wrap">
          <PinInput
            length={8}
            value={emailLoginCode}
            onChange={setEmailLoginCode}
            size="sm"
            placeholder={isRussianSpeaking ? "" : ""}
          />
          <Button
            onClick={handleLoginWithCode}
            variant="light"
            color="indigo"
            size="sm"
          >
            {isRussianSpeaking ? "Войти" : "Log in"}
          </Button>
        </Group>
      )}
      {emailLoginError && (
        <Text size="sm" c="red" ta="center">
          {emailLoginError}
        </Text>
      )}
    </Stack>
  );

  return (
    <Container size="md" py={{ base: "lg", sm: "xl" }}>
      <Stack gap="xl">
        <Paper radius="xl" p={{ base: "lg", sm: "xl" }} shadow="xs" withBorder>
          <Stack gap="md" align="center">
            <Title order={2}>
              {isRussianSpeaking ? "Ваша библиотека" : "Your library"}
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              {isRussianSpeaking
                ? "Собирайте фильмы, книги и игры, отмечайте тэгами и делитесь с друзьями."
                : "Collect movies, books, and games, tag them, and share with friends."}
            </Text>
            <Divider size="xs" w="40%" />
            {renderLoginOptions()}
          </Stack>
        </Paper>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
          <Paper radius="lg" p="lg" shadow="xs" withBorder>
            <Text fw={600} mb={6}>
              {isRussianSpeaking ? "Организуйте" : "Organize"}
            </Text>
            <Text size="sm" c="dimmed">
              {isRussianSpeaking
                ? "Все, что вам нравится, в одном аккуратном месте."
                : "Everything you love, kept in one clean space."}
            </Text>
          </Paper>
          <Paper radius="lg" p="lg" shadow="xs" withBorder>
            <Text fw={600} mb={6}>
              {isRussianSpeaking ? "Обменивайтесь" : "Share"}
            </Text>
            <Text size="sm" c="dimmed">
              {isRussianSpeaking
                ? "Рекомендации между друзьями без шумных лент."
                : "Friend-to-friend recommendations without noisy feeds."}
            </Text>
          </Paper>
          <Paper radius="lg" p="lg" shadow="xs" withBorder>
            <Text fw={600} mb={6}>
              {isRussianSpeaking ? "Возвращайтесь" : "Revisit"}
            </Text>
            <Text size="sm" c="dimmed">
              {isRussianSpeaking
                ? "Логбук напоминает, что уже посмотрели или прочли."
                : "A logbook that remembers what you watched or read."}
            </Text>
          </Paper>
        </SimpleGrid>
      </Stack>
    </Container>
  );
};

const PersonalArtEventsWithContext = ({ eventType, userId }) => {
  const { repeatedEnter } = useContext(MainPageUIContext);
  return (
    <PersonalArtEvents repeatedEnter={repeatedEnter} eventType={eventType} userId={userId} />
  );
};

function MainPage() {
  const { setColorScheme } = useMantineColorScheme();
  const [isRussianSpeaking, setIsRussianSpeaking] = useState(true); // TODO

  const [loginState, setLoginState] = useState(null); // also, loggedIn and loggedOut
  const [oauthLoginURL, setOauthLoginURL] = useState(null);
  const [emailLogin, setEmailLogin] = useState("");
  const [emailLoginCode, setEmailLoginCode] = useState("");
  const [emailLoginMessage, setEmailLoginMessage] = useState("");
  const [emailLoginError, setEmailLoginError] = useState("");
  const [loginCodeRequested, setLoginCodeRequested] = useState(false);

  const [myUserInfo, setMyUserInfo] = useState(null);
  const [myUserSettings, setMyUserSettings] = useState(null);

  // const [jwtObtained, setJWTObtained] = useState(false); // TODO: better use one more loginState (oauthRedirecting?)

  const [unreadCount, setUnreadCount] = useState(null);

  const [selectedTab, setSelectedTab] = useState(0); // profile page is 0, increments to the right
  const [repeatedEnter, setRepeatedEnter] = useState(0);

  const updateMyUserInfo = () => {
    axiosInstance
    .get(`/checkLoggedIn`)
    .then((response) => {     
      setMyUserInfo(response.data);
      setLoginState('loggedIn');
    })
    .catch(error => {
      if (error.response.status === 401) {
        setLoginState('loggedOut');
        // setJWTObtained(false);
      } else {
        // console.error('Error checking login status:', error);  
      }
    });
  }

  const fetchMyUserSettings = () => {
    axiosInstance
    .get(`/user/settings`)
    .then((response) => {
      if (response.status === 200) {
        setMyUserSettings(response.data);
        if (response.data.theme) {
          setColorScheme(response.data.theme);
        }
      }
    })
    .catch(error => {
      // console.error('Error checking login status:', error);
    });
  }
  const updateMyUserSettings = (lang, username, avatarUrl) => {
    setMyUserSettings({
      'lang': lang,
      'userName': username,
      'avatarUrl': avatarUrl
    })
  }

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    setLoginState('loggedOut');
    setMyUserInfo(null);
    setMyUserSettings(null);
    setUnreadCount(null);
  };

  const oauthLoginURLReceivedCallback = (url) => {
    setOauthLoginURL(url);
  }
  const handleLoginWithGoogle = () => {
    window.location.href = oauthLoginURL;
  };

  const fetchUnreadCount = () => {
    axiosInstance
    .get('/notifications/unreadCount')
    .then((response) => {
      setUnreadCount(response.data.unreadCount);
    })
    .catch(error => {
      // console.error('Failed to fetch unread notification count:', error);
    })
  };

  // Google login.
  const [ user, setUser ] = useState(null);
  const login = useGoogleLogin({
      onSuccess: (codeResponse) => setUser(codeResponse),
      onError: (error) => console.log('Login Failed:', error)
  });
  useEffect(
    () => {
        if (user) {
            oauth.ProvideTokenToBackend({token: user.access_token, onJWTObtained: () => {
              // setJWTObtained(true);
              updateMyUserInfo();
              window.location.href = "/";
            }
            });
        }
    },
    [ user ]
  );

  const handleEmailLoginChange = (event) => {
    setEmailLogin(event.target.value);
    setLoginCodeRequested(false);
    setEmailLoginCode("");
    setEmailLoginMessage("");
    setEmailLoginError("");
  };

  const getLoginCodeSentMessage = (sentTo) => {
    if (!Array.isArray(sentTo) || sentTo.length === 0) {
      return {
        text: isRussianSpeaking
          ? "Код входа не удалось отправить. Попробуйте позже"
          : "Login code could not be delivered. Please try again",
        isError: true
      };
    }

    const labels = [];
    if (sentTo.includes("tg")) {
      labels.push(isRussianSpeaking ? "в Телеграм" : "to Telegram");
    }
    if (sentTo.includes("email")) {
      labels.push(isRussianSpeaking ? "на почту" : "to email");
    }

    if (labels.length === 0) {
      return {
        text: isRussianSpeaking
          ? "Код входа не удалось отправить. Попробуйте позже"
          : "Login code could not be delivered. Please try again",
        isError: true
      };
    }

    const joiner = isRussianSpeaking ? " и " : " and ";
    const channelList = labels.join(joiner);
    return {
      text: isRussianSpeaking
        ? `Код входа отправлен ${channelList}`
        : `Login code sent ${channelList}`,
      isError: false
    };
  };

  const handleSendLoginCode = () => {
    const emailValue = emailLogin.trim();
    if (!emailValue) {
      setEmailLoginError("Email is required");
      return;
    }
    setEmailLoginError("");
    setEmailLoginMessage("");
    oauth.SendLoginCode({
      email: emailValue,
      onSent: (sentTo) => {
        const messageInfo = getLoginCodeSentMessage(sentTo);
        setEmailLoginMessage(messageInfo.text);
        setEmailLoginError(messageInfo.isError ? messageInfo.text : "");
        setLoginCodeRequested(!messageInfo.isError);
      }
    }).catch(() => {
      setEmailLoginError((isRussianSpeaking ? "Не удалось отправить код для входа" : "Could not send login code"));
      setLoginCodeRequested(false);
    });
  };

  const handleLoginWithCode = () => {
    const emailValue = emailLogin.trim();
    const codeValue = emailLoginCode.trim();
    if (!emailValue || !codeValue) {
      setEmailLoginError("Email and code are required");
      return;
    }
    setEmailLoginError("");
    oauth.LoginWithCode({
      email: emailValue,
      code: codeValue,
      onJWTObtained: () => {
        updateMyUserInfo();
        window.location.href = "/";
      }
    }).catch(() => {
      setEmailLoginError(isRussianSpeaking ? "Код неверен или истек" : "Code is wrong or expired");
    });
  };

  // Initialize Google Analytics
  useEffect(() => {
    ReactGA.initialize('G-SRJGEMSELK'); // Replace with your Google Analytics Measurement ID
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
  }, []);

  const mainPageContextValue = {
    selectedTab,
    setSelectedTab,
    repeatedEnter,
    setRepeatedEnter,
    myUserSettings,
    myUserInfo,
    unreadCount,
    isRussianSpeaking,
    emailLogin,
    emailLoginCode,
    emailLoginMessage,
    emailLoginError,
    loginCodeRequested,
    updateMyUserInfo,
    handleEmailLoginChange,
    setEmailLoginCode,
    handleSendLoginCode,
    handleLoginWithCode,
    handleLogout
  };

  const routerLoggedOut = useMemo(() => {
    return createBrowserRouter([
      {
        path: "/",
        element: <Layout loggedIn={false}/>,
        children: [
          {
            path: "/",
            element: <LoggedOutHome />
          },
          {
            path: "/users",
            element: <Users />
          },
          {
            path: "/addEvent",
            element: <AddEvent />
          },
          {
            path: "/notifications",
            element: <Notifications />
          },
          {
            path: "/arts/:id",
            element: <ArtPage />
          },
          {
            path: "/users/:pathUserId/likes",
            element: <PersonalArtEventsWithContext eventType="like" userId={null} />
          },
          {
            path: "/logbookHistory",
            element: <LogbookHistory />
          },
          {
            path: "/artEvents/:id",
            element: <ArtEvent eventType="artEvent"/>
          },
          {
            path: "/newUser",
            element: <NewUser />
          },
          {
            path: "/artByLink",
            element: <ArtPage />
          }
        ]
      }
    ]);
  }, []);

  // // TODO: refactor, this block is ugly.
  // let isOauthRedirect = false;
  // const urlParams = new URLSearchParams(window.location.search);
  // const code = urlParams.get('code');
  // if (code !== null) {
  //   isOauthRedirect = true;
  // }

  // Sync Telegram theme with Mantine color scheme (only inside TG Mini App, only if user preference is "auto").
  useEffect(() => {
    const tgWebApp = window.Telegram?.WebApp;
    if (!tgWebApp || !tgWebApp.initData) return;
    const savedTheme = myUserSettings?.theme;
    if (savedTheme && savedTheme !== 'auto') return; // user has explicit preference, don't override
    if (tgWebApp.colorScheme) {
      setColorScheme(tgWebApp.colorScheme);
    }
    const onThemeChanged = () => {
      if (tgWebApp.colorScheme) {
        setColorScheme(tgWebApp.colorScheme);
      }
    };
    tgWebApp.onEvent('themeChanged', onThemeChanged);
    return () => tgWebApp.offEvent('themeChanged', onThemeChanged);
  }, [setColorScheme, myUserSettings]);

  // Telegram Web App auto-auth.
  const [tgAuthAttempted, setTgAuthAttempted] = useState(false);
  useEffect(() => {
    const tgWebApp = window.Telegram?.WebApp;
    if (tgWebApp && tgWebApp.initData && !tgAuthAttempted) {
      setTgAuthAttempted(true);
      tgWebApp.ready();
      tgWebApp.expand();
      oauth.TelegramWebAppAuth({
        initData: tgWebApp.initData,
        onJWTObtained: () => {
          updateMyUserInfo();
          fetchMyUserSettings();
          fetchUnreadCount();
        }
      }).catch(() => {
        // Fallback to normal auth check if TG auth fails.
        updateMyUserInfo();
        fetchMyUserSettings();
        fetchUnreadCount();
      });
      return;
    }
  }, [tgAuthAttempted]);

  useEffect(() => {
    if (tgAuthAttempted) return; // TG auth handles its own init.
    if (loginState === null && myUserSettings === null && unreadCount === null) {
      updateMyUserInfo();
      fetchMyUserSettings();
      fetchUnreadCount();
    }
  }, [loginState, myUserSettings, unreadCount, tgAuthAttempted]);

  if (!loginState) {
    return <div></div>
  }
  if (loginState === 'loggedIn' && !myUserSettings) { // if logged in, wait for settings
    return <div></div>
  }
  // loginStaet === 'loggedOut' -> myUserSettings are not needed

  // if (loginState === 'unknown' && !isOauthRedirect || (loginState === "loggedIn" && (myUserSettings === null || unreadCount === null))) {
  //   return <div></div>
  // }

  if (loginState === 'loggedOut') {
    return (
      <MainPageUIContext.Provider value={mainPageContextValue}>
        <RouterProvider router={routerLoggedOut}>
        </RouterProvider>
      </MainPageUIContext.Provider>
    );
  }

  const routerLoggedIn = createBrowserRouter([
    {
      path: "/",
      element: <Layout loggedIn={true}/>,
      children: [
        {
          path: "/",
          element: <Navigate to={`/notifications`} replace />,
        },
        {
          path: "/addEvent",
          element: <AddEvent repeatedEnter={repeatedEnter}/>
        },
        {
          path: "/users/:pathUserId",
          element: <UserPage userId={null} />
        },
        {
          path: "/users/:pathUserId/likes",
          element: <PersonalArtEvents repeatedEnter={repeatedEnter} eventType="like" key="someonesLikes" userId={null} />
        },
        {
          path: "/users/:pathUserId/recommendationsIncoming",
          element: <PersonalArtEvents repeatedEnter={repeatedEnter} eventType="recommendationIncoming" key="someonesIncomingRecommendations" userId={null} />
        },
        {
          path: "/users/:pathUserId/recommendationsOutgoing",
          element: <PersonalArtEvents eventType="recommendationOutgoing" key="someonesOutgoingRecommendations" userId={null} />
        },
        // {
        //   path: "/recommendations/toMe",
        //   element: <RecommendationEvents key="recomToMe" params={{fromMe: false}} />
        // },
        // {
        //   path: "/recommendations/fromMe",
        //   element: <RecommendationEvents key="recomFromMe"  params={{fromMe: true}} />
        // },
        {
          path: "/users",
          element: <Users />
        },
        {
          path: "/settings",
          element: <Settings onSettingsChange={updateMyUserSettings} onLogout={handleLogout} />
        },
        {
          path: "/logbookHistory",
          element: <LogbookHistory />
        },
        {
          path: "/artEvents/:id",
          element: <ArtEvent eventType="artEvent"/>
        },
        {
          path: "/logbook",
          element: <LogbookPage />
        },
        {
          path: "/recommendations/:id",
          element: <ArtEvent eventType="recommendation"/>
        },
        {
          path: "/arts/:id",
          element: <ArtPage />
        },
        {
          path: "/notifications",
          element: <Notifications unreadCountCallback={setUnreadCount} />
        },
        {
          path: "/users/:pathUserId/recalc",
          element: <RecalcUser />
        },
        {
          path: "/artByLink",
          element: <ArtPage />
        }
      ]
    }
  ]);

  return (
    <MainPageUIContext.Provider value={mainPageContextValue}>
      <MyUserContext.Provider value={{myUserInfo: myUserInfo, myUserSettings: myUserSettings}}>
        <RouterProvider router={routerLoggedIn}>
        </RouterProvider>
      </MyUserContext.Provider>
    </MainPageUIContext.Provider>
  );
  // TODO: Put in footer: Icons by <a target="_blank" href="https://icons8.com">Icons8</a>
}

export default MainPage;
